import { useState, useEffect, useMemo } from 'react';
import { DataGridIdentifiers } from '../Interface/Datagrid/DataGridIdentifiers';
import { Field, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography } from '@mui/material';

import { CheckboxUI, DeleteDialogUI, FileTabUI, FormErrorUI, FormLabelUI, InputUI, TabsUI } from '../Interface';
import DocumentLine from './DocumentLine';
import DocumentDescription from './DocumentDescription';
import DocumenLogistics from './DocumenLogistics';
import DocumentAdditional from './DocumentAdditional';
import DocumentSum from './DocumentSum';
import jokerService from '../../service/joker.service';
import JokerTab from '../Interface/Joker/JokerTab';
import useParam from '../../context/ParamContext';
import { documentTabErrorFieldSets } from '../../config/validation/documentValidationConfig';
import Comments from '../Comment/Comments';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const hasNestedErrors = (value) => {
  if (!value) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some((item) => hasNestedErrors(item));
  if (typeof value === 'object') return Object.values(value).some((item) => hasNestedErrors(item));
  return false;
};

const getValueByPath = (source, path) => {
  if (!path || typeof path !== 'string') return undefined;
  return path.split('.').reduce((acc, key) => acc?.[key], source);
};

const CompanyWarningPanel = ({ disabled }) => {
  const { values, errors } = useFormikContext();

  return (
    <div>
      <div className="col-span-full relative">
        <div className="mt-2">
          <Field
            type="text"
            name="companyWarning"
            as={InputUI}
            className={`${disabled ? 'rounded-md' : ''} resize-none h-36, p-2`}
            fullWidth
            label={<FormLabelUI text="Figyelmeztetés" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.companyWarning} />}
            disabled={disabled}
            InputLabelProps={{ shrink: values.companyWarning !== '' }}
            multiline
            rows={10}
          />
        </div>
      </div>
    </div>
  );
};

const DocumentTabs = (props) => {
  const { params } = useParam();
  const {
    newFilesState,
    disabled,
    attachmentEditMode = false,
    docTypeId,
    selectedValues,
    dialog,
    setRefetchTable,
    addField,
  } = props;
  const { newFiles, setNewFiles } = newFilesState;
  const { values, setFieldValue, setValues, errors, submitCount } = useFormikContext();
  const [fileToDelete, setFileToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const fileTabDisabled = attachmentEditMode ? false : disabled;

  const [jokerGroups, setJokerGroups] = useState([]);

  const getJokerGroups = async () => {
    jokerService.getGroups(props.upperDocType).then((data) => {
      setJokerGroups(data);
    });
  };

  useEffect(() => {
    getJokerGroups();
  }, []);

  const tabHasError = (tab) => {
    if (tab.key === 'LINE') return hasNestedErrors(errors?.docLine);
    if (documentTabErrorFieldSets[tab.key]) {
      return Array.from(documentTabErrorFieldSets[tab.key]).some((field) => hasNestedErrors(errors?.[field]));
    }
    if (tab.key === 'ATTACHMENT') return hasNestedErrors(errors?.attachment);
    if (tab.key === 'WARNING') return hasNestedErrors(errors?.companyWarning);
    if (tab.key.startsWith('JOKER_')) {
      return tab.errorPaths?.some((path) => path && hasNestedErrors(getValueByPath(errors, path)));
    }

    return false;
  };

  const renderTabName = (tab) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <span>{tab.name}</span>
      {tab.hasError && (
        <Typography
          component="span"
          sx={{ fontWeight: 700, color: 'error.main', lineHeight: 1 }}
          aria-label={`${tab.name} hiba`}
        >
          !
        </Typography>
      )}
    </Box>
  );

  const handleUpload = (files) => {
    const filesWithId = files.map((file) => {
      file.id = uuidv4();
      return file;
    });

    const updatedAttachments = (values.attachment || []).filter(
      (attachment) => !filesWithId.some((file) => file.name === attachment.name)
    );
    setFieldValue('attachment', updatedAttachments);

    const existingFiles = newFiles.filter(
      (existingFile) => !filesWithId.some((file) => file.name === existingFile.name)
    );

    setNewFiles([...existingFiles, ...filesWithId]);
  };

  const hideDeleteDialog = () => {
    setFileToDelete(null);
  };

  const handleRemoveElement = () => {
    setNewFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileToDelete));
    setValues((currentValues) => ({
      ...currentValues,
      attachment: (currentValues.attachment || []).filter((attachment) => attachment.id !== fileToDelete),
      deletedAttachmentIds:
        Number.isInteger(fileToDelete) &&
        (currentValues.attachment || []).some((attachment) => attachment.id === fileToDelete)
          ? [...new Set([...(currentValues.deletedAttachmentIds || []), fileToDelete])]
          : currentValues.deletedAttachmentIds || [],
    }));

    hideDeleteDialog();
  };

  const tabs = [
    {
      id: 0,
      name: 'Tétel',
      content: <DocumentLine {...props} />,
      key: 'LINE',
    },
    {
      id: 1,
      name: 'Logisztika',
      content: <DocumenLogistics {...props} />,
      key: 'LOGISTICS',
    },
    {
      id: 2,
      name: 'További adatok',
      content: <DocumentAdditional {...props} />,
      key: 'ADDITIONAL',
    },
    {
      id: 3,
      name: 'Megjegyzés',
      content: <DocumentDescription {...props} />,
      key: 'DESCRIPTION',
    },

    {
      id: 4,
      name: 'Összesen',
      content: <DocumentSum {...props} />,
      key: 'SUM',
    },
    {
      id: 5,
      name: 'Csatolmányok',
      content: (
        <FileTabUI
          datagridIdentifier={DataGridIdentifiers.uploadFileWithUpdatedAt}
          disabled={fileTabDisabled}
          savedFiles={[...(values?.attachment || []), ...newFiles]}
          handleUpload={handleUpload}
          handleDelete={(item) => {
            if (
              item.isDigitallySigned ||
              item.isImmutablePdf ||
              item.isElectronicInvoiceHash ||
              item.isElectronicInvoiceXml
            ) {
              addNotification({
                content: 'A végleges PDF, a hashfájl és az APEH XML nem törölhető.',
                type: NotificationType.WARNING,
              });
              return;
            }
            setFileToDelete(item.id);
          }}
          showUpdatedAt={true}
          additionalColumns={[
            {
              field: 'isDigitallySigned',
              headerName: 'E-aláírt',
              type: 'boolean',
              flex: 0.35,
              valueGetter: (params) => Boolean(params.row.isDigitallySigned),
            },
            {
              field: 'isSent',
              headerName: 'Kiküldött',
              flex: 0.35,
              sortable: false,
              renderCell: (params) => {
                const attachmentIndex = (values.attachment || []).findIndex(
                  (attachment) => attachment.id === params.row.id
                );
                const attachment = values.attachment?.[attachmentIndex];

                return (
                  <div onClick={(event) => event.stopPropagation()}>
                    <CheckboxUI
                      checked={Boolean(attachment?.isSent)}
                      disabled={
                        fileTabDisabled ||
                        attachmentIndex < 0 ||
                        Boolean(
                          attachment?.isDigitallySigned ||
                            attachment?.isImmutablePdf ||
                            attachment?.isElectronicInvoiceHash ||
                            attachment?.isElectronicInvoiceXml
                        )
                      }
                      onCheckedChange={(checked) => {
                        if (
                          attachmentIndex < 0 ||
                          attachment?.isDigitallySigned ||
                          attachment?.isImmutablePdf ||
                          attachment?.isElectronicInvoiceHash ||
                          attachment?.isElectronicInvoiceXml
                        )
                          return;
                        setFieldValue(`attachment.${attachmentIndex}.isSent`, checked);
                      }}
                    />
                  </div>
                );
              },
            },
          ]}
        />
      ),
      key: 'ATTACHMENT',
    },
    {
      id: 6,
      name: 'Figyelmeztetés',
      content: <CompanyWarningPanel {...props} />,
      key: 'WARNING',
    },
    {
      id: 7,
      name: 'Hozzászólások',
      content: (
        <Comments
          rows={selectedValues.comments ?? []}
          lineId={selectedValues.docId}
          docTypeId={docTypeId}
          setRefetchTable={dialog ? dialog?.setTaskChanged : setRefetchTable}
          disabled={!selectedValues.docId || !addField}
        />
      ),
      key: 'COMMENT',
    },
    ...((jokerGroups.length > 0 &&
      jokerGroups
        .filter((group) => group.jokerFields.length > 0)
        .map((group, index) => ({
          id: index + 8,
          name: group.name,
          key: `JOKER_${group.id}`,
          errorPaths: group.jokerFields.map((field) => field.jokerField?.field).filter(Boolean),
          content: <JokerTab fields={group.jokerFields} key={index} {...props} />,
        }))) ||
      []),
  ];

  const visibleTabs = useMemo(() => {
    const parsedVisibility = JSON.parse(params.DOCUMENT_TAB_FIELDS ?? '{}');
    const parsedOrder = JSON.parse(params.DOCUMENT_TAB_ORDER ?? '{}');

    const visibilityConfig = parsedVisibility[docTypeId] || [];
    const orderConfig = parsedOrder[docTypeId] || [];

    const tabVisibilityMap = Object.assign({}, ...(visibilityConfig || []));
    const tabOrderMap = Object.assign({}, ...(orderConfig || []));

    const filteredTabs = tabs.filter((tab) => {
      if (!params.DOCUMENT_TAB_FIELDS) {
        return true;
      }
      return !tabVisibilityMap[tab.key] || tabVisibilityMap[tab.key] === 'Y';
    });

    const ordered = [];
    const unordered = [];

    filteredTabs.forEach((tab) => {
      if (tabOrderMap[tab.key] !== undefined) {
        ordered.push({
          ...tab,
          visOrder: tabOrderMap[tab.key],
        });
      } else {
        unordered.push(tab);
      }
    });

    ordered.sort((a, b) => a.visOrder - b.visOrder);

    return [...ordered, ...unordered].map((tab) => ({
      ...tab,
      hasError: tabHasError(tab),
      renderedName: renderTabName({ ...tab, hasError: tabHasError(tab) }),
    }));
  }, [params.DOCUMENT_TAB_FIELDS, params.DOCUMENT_TAB_ORDER, tabs, docTypeId, errors]);

  useEffect(() => {
    if (activeTab >= visibleTabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, visibleTabs.length]);

  useEffect(() => {
    if (!submitCount) return;

    const firstErrorTabIndex = visibleTabs.findIndex((tab) => tab.hasError);
    if (firstErrorTabIndex >= 0) {
      setActiveTab(firstErrorTabIndex);
    }
  }, [submitCount, visibleTabs]);

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  return (
    <>
      <div className="my-4">
        <div className="px-3">
          <TabsUI activeTab={activeTab} handleTabClick={handleTabClick}>
            {visibleTabs.map((tab, index) => (
              <div key={index} name={tab.renderedName}>
                {tab.content}
              </div>
            ))}
          </TabsUI>
        </div>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={fileToDelete}
        onHide={() => {
          hideDeleteDialog();
        }}
      />
    </>
  );
};

export default DocumentTabs;
